/**
 * Countries, states and cities for the Location attribute (5 Oct 2026).
 *
 * Data: `countrycitystatejson` (MIT — the popular `country-state-city` is GPL-3.0, which a
 * commercial product cannot ship). Nothing loads until a location field is used:
 *   - countries + states: one ~300 KB chunk, on first use;
 *   - cities: one chunk per COUNTRY (India ≈ 280 KB), only when that country's cities are needed.
 *
 * Values are stored by NAME ({ country, state, city }), not code — that is what members,
 * rules and reports read. The data is uneven for some countries (no cities for Abu Dhabi;
 * two spellings of Sharjah), so the city box also accepts a city typed by hand.
 */

import { useEffect, useState } from 'react'

type Lib = typeof import('countrycitystatejson/client')
export interface Country { code: string; name: string; emoji?: string }

let lib: Lib | null = null
let loading: Promise<Lib> | null = null
let countryList: Country[] | null = null
const byName = new Map<string, Country>()
const cityCache = new Map<string, string[]>()

export function loadPlaces(): Promise<Lib> {
  loading ??= import('countrycitystatejson/client').then(m => {
    lib = m
    countryList = m.getCountries()
      .filter(c => !!c.name)
      .map(c => ({ code: c.shortName, name: c.name!, emoji: c.emoji }))
      .sort((a, b) => a.name.localeCompare(b.name))
    countryList.forEach(c => byName.set(c.name, c))
    return m
  })
  return loading
}

/** True once countries and states are available (starts loading on first call). */
export function usePlacesReady(): boolean {
  const [ready, setReady] = useState(!!lib)
  useEffect(() => { if (!lib) void loadPlaces().then(() => setReady(true)) }, [])
  return ready
}

export function countries(): Country[] { return countryList ?? [] }
export function countryNamed(name: string | undefined): Country | undefined { return name ? byName.get(name) : undefined }

export function statesOf(countryName: string | undefined): string[] {
  const c = countryNamed(countryName)
  if (!lib || !c) return []
  return [...(lib.getStatesByShort(c.code) ?? [])].sort((a, b) => a.localeCompare(b))
}

/**
 * Each country's cities, one file per country, loaded by VITE rather than the library's own
 * loader (6 Oct 2026). The library imports them as JSON modules (`with { type: 'json' }`); the
 * dev server answers those with JavaScript, the browser refuses the mismatch, and the city box
 * sat on "Loading…" for ever. import.meta.glob gives Vite-made loaders that work in dev and build.
 */
type CountryFile = { states?: Record<string, Array<{ name?: string }>> }
const COUNTRY_FILES = import.meta.glob<CountryFile>('../../node_modules/countrycitystatejson/dist/esm/lib/by-country/*.json', { import: 'default' })
const fileFor = (code: string) => Object.entries(COUNTRY_FILES).find(([path]) => path.endsWith(`/${code}.json`))?.[1]

export async function citiesOf(countryName: string, state: string): Promise<string[]> {
  const key = `${countryName}|${state}`
  const hit = cityCache.get(key)
  if (hit) return hit
  await loadPlaces()
  const c = countryNamed(countryName)
  const load = c ? fileFor(c.code) : undefined
  let list: string[] = []
  try {
    const data = load ? await load() : null
    list = [...new Set((data?.states?.[state] ?? []).map(x => x.name).filter((n): n is string => !!n))].sort((a, b) => a.localeCompare(b))
  } catch (e) {
    // No list is not an error the person can fix — they can still type the city.
    console.warn('[places] could not load cities for', countryName, (e as Error).message)
  }
  cityCache.set(key, list)
  return list
}

/** Cities for several (country, state) pairs at once — `null` while loading. */
export function useCities(pairs: Array<{ country: string; state: string }>): Map<string, string[]> | null {
  const sig = pairs.map(p => `${p.country}|${p.state}`).sort().join('\n')
  const [out, setOut] = useState<{ sig: string; map: Map<string, string[]> } | null>(null)
  useEffect(() => {
    let live = true
    const list = sig ? sig.split('\n').map(s => { const [country, state] = s.split('|'); return { country, state } }) : []
    void Promise.all(list.map(async p => [`${p.country}|${p.state}`, await citiesOf(p.country, p.state)] as const))
      .then(rows => { if (live) setOut({ sig, map: new Map(rows) }) })
    return () => { live = false }
  }, [sig])
  return out && out.sig === sig ? out.map : null
}
